import React, { useState, useEffect, useCallback } from "react";
import { Modal, Button, Form, Spinner, Row, Col } from "react-bootstrap";
import Select from "react-select";
import Multiselect from "multiselect-react-dropdown";
import FormSelect from "components/elements/form-select/FormSelect";
import { editUser, getEmployeeByIdAPI } from "ServerAPI/serverAPI";
import { roleFeatureMapping, roleIdWithName } from "constants/Constants";
import { useToast } from "context/toast-context/ToastProvider";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import BusinessIcon from "@mui/icons-material/Business";
import WorkIcon from "@mui/icons-material/Work";
import FeaturesIcon from "@mui/icons-material/Extension";
import ToggleOnIcon from "@mui/icons-material/ToggleOn";
import ToggleOffIcon from "@mui/icons-material/ToggleOff";
import BadgeIcon from "@mui/icons-material/Badge";
import { useResponsive } from "components/ResponsiveDesign/ResponsiveDesign";

const EditUserModal = ({
    open,
    handleClose,
    userForEdit,
    organizationDetails,
    allDepartments,
    fetchUsers,
    cookies,
}) => {
    const { isMobile, isTablet } = useResponsive();
    const [userType, setUserType] = useState(cookies.userManagement === "HRMS" ? "guest" : "normal");
    const [selectRow, setSelectRow] = useState(null);
    const [editedRow, setEditedRow] = useState({});
    const [originalRow, setOriginalRow] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUserUpdated, setIsUserUpdated] = useState(false);
    const [showStatusConfirmation, setShowStatusConfirmation] = useState(false);
    const [features, setFeatures] = useState([]);
    const [filteredFeatures, setFilteredFeatures] = useState([]);
    const [existingFeatures, setExistingFeatures] = useState([]);
    const [isLoadingEmployeeData, setIsLoadingEmployeeData] = useState(false);
    const { showToast } = useToast();

    const handleUserModalClose = () => {
        handleClose();
        setEditedRow({});
        setSelectRow(null);
        setOriginalRow({});
        setShowStatusConfirmation(false);
        setIsLoadingEmployeeData(false);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if (name === "roleId") {
            const selectedRole = roleIdWithName.find((role) => role.id === value);
            setEditedRow((prev) => ({
                ...prev,
                roleId: value,
                roleName: selectedRole?.name || "",
            }));
        } else {
            setEditedRow((prev) => ({ ...prev, [name]: value }));
        }
    };

    const handleStatusToggle = () => {
        const newStatus = editedRow.status === "active" ? "inactive" : "active";
        setEditedRow((prev) => ({ ...prev, status: newStatus }));
    };

    const hasStatusChanged = () => {
        return originalRow.status !== editedRow.status;
    };

    const handleSaveClick = () => {
        if (hasStatusChanged()) {
            setShowStatusConfirmation(true);
        } else {
            handleSave();
        }
    };

    const handleSave = async () => {
        setIsSubmitting(true);
        try {
            // Ensure roleName is always set if roleId is present
            let roleName = editedRow.roleName;
            if (!roleName && editedRow.roleId) {
                const foundRole = roleIdWithName.find((role) => role.id === editedRow.roleId);
                roleName = foundRole ? foundRole.name : "";
            }

            const action = hasStatusChanged() ? "TOGGLE" : "MODIFY";
            const payload = {
                userId: editedRow?.userId,
                name: editedRow.name,
                email: editedRow.email,
                phoneNumber: editedRow.phone,
                status: editedRow.status,
                authorUserId: cookies.userId,
                action: action,
                featureIds: existingFeatures.map((feature) => feature?.value),
                department: editedRow.department,
                role: {
                    roleId: editedRow.roleId,
                    roleName: roleName,
                },
                userManagement: cookies.userManagement,
            };
            console.log("editedRow", editedRow);
            console.log("payload", payload);
            // return;

            await editUser(payload);
            fetchUsers();
            handleUserModalClose();
            showToast("User updated successfully", "success");
            setIsUserUpdated(true);
        } catch (error) {
            console.error("Error saving user", error);
            showToast("Error updating user", "error");
        } finally {
            setIsSubmitting(false);
        }
    };

    const fetchEmployeeData = async (userId) => {
        if (cookies?.userManagement === "HRMS" && userId) {
            setIsLoadingEmployeeData(true);
            try {
                const response = await getEmployeeByIdAPI(userId, cookies?.token);

                if (response.status === 200 && response.data.status === "success") {
                    const employeeData = response.data.data;
                    return {
                        name: employeeData.name,
                        email: employeeData.email,
                    };
                } else {
                    if (response?.data?.message) {
                        showToast(response.data.message, "error");
                    } else {
                        showToast("Failed to fetch employee data", "error");
                    }
                }
            } catch (error) {
                console.error("Error fetching employee data:", error);
                const errMessage =
                    error?.response?.data?.message ||
                    "Something went wrong while fetching employee data.";
                showToast(errMessage, "error");
            } finally {
                setIsLoadingEmployeeData(false);
            }
        }
        return null;
    };

    const filterFeaturesByRole = (selectedRoleId, allFeatures) => {
        if (!selectedRoleId || !allFeatures) return [];
        const allowedFeatures = roleFeatureMapping[selectedRoleId] || [];
        return allFeatures.filter((feature) =>
            allowedFeatures.includes(feature.label)
        );
    };

    const initializeModalData = async () => {
        setSelectRow(userForEdit);
        setOriginalRow(userForEdit);

        let initialEditedRow = { ...userForEdit };

        if (cookies?.userManagement === "HRMS") {
            try {
                const employeeData = await fetchEmployeeData(userForEdit.userId);
                if (employeeData) {
                    initialEditedRow.name = employeeData.name;
                    initialEditedRow.email = employeeData.email;
                }
            } catch (err) {
                console.error("Error fetching employee data", err);
            }
        }

        setEditedRow(initialEditedRow);

        const orgFeatures = organizationDetails?.features || [];
        const formattedFeatures = orgFeatures.map((f) => ({
            value: f.featureId,
            label: f.featureName,
        }));
        setFeatures(formattedFeatures);

        const userFeatureIds = userForEdit?.features || [];
        const userExistingFeatures = orgFeatures
            .filter((orgFeature) => userFeatureIds.includes(orgFeature.featureId))
            .map((f) => ({
                value: f.featureId,
                label: f.featureName,
            }));

        setExistingFeatures(userExistingFeatures);

        const filtered = filterFeaturesByRole(userForEdit.roleId, formattedFeatures);
        setFilteredFeatures(filtered);

        const validFeatures = userExistingFeatures.filter((selectedFeature) =>
            filtered.some(
                (availableFeature) => availableFeature.value === selectedFeature.value
            )
        );
        setExistingFeatures(validFeatures);
    };

    useEffect(() => {
        if (userForEdit && open) {
            initializeModalData();
        }
    }, [userForEdit, open, cookies?.userManagement, organizationDetails]);

    useEffect(() => {
        const selectedRole = editedRow?.roleId;
        const filtered = filterFeaturesByRole(selectedRole, features);
        setFilteredFeatures(filtered);

        if (existingFeatures && existingFeatures.length > 0) {
            const validFeatures = existingFeatures.filter((selectedFeature) =>
                filtered.some(
                    (availableFeature) => availableFeature.value === selectedFeature.value
                )
            );
            if (validFeatures.length !== existingFeatures.length) {
                setExistingFeatures(validFeatures);
            }
        }
    }, [editedRow?.roleId, features]);

    const formFields = [
        {
            id: "formName",
            label: "Name",
            name: "name",
            type: "text",
            value: editedRow.name || "",
            disabled: cookies.userManagement?.trim().toUpperCase() === "HRMS",
            icon: <PersonIcon style={{ color: "#224D99", fontSize: "18px" }} />,
        },
        {
            id: "formLastName",
            label: "Last Name",
            name: "lastName",
            type: "text",
            value: editedRow.lastName || "",
            disabled: cookies.userManagement?.trim().toUpperCase() === "HRMS",
            icon: <PersonIcon style={{ color: "#224D99", fontSize: "18px" }} />,
        },
        ...(userType === "guest"
            ? [
                {
                    id: "formUserId",
                    label: "Employee ID",
                    name: "userId",
                    type: "text",
                    value: editedRow.userId || "",
                    disabled: true,
                    icon: <BadgeIcon style={{ color: "#224D99", fontSize: "18px" }} />,
                },
            ]
            : []),
        {
            id: "formEmail",
            label: "Email",
            name: "email",
            type: "email",
            value: editedRow.email || "",
            disabled: cookies.userManagement?.trim().toUpperCase() === "HRMS",
            icon: <EmailIcon style={{ color: "#224D99", fontSize: "18px" }} />,
        },
        ...(userType === "normal"
            ? [
                {
                    id: "formPhone",
                    label: "Phone",
                    name: "phone",
                    type: "text",
                    value: editedRow.phone || "",
                    disabled: false,
                    icon: <PhoneIcon style={{ color: "#224D99", fontSize: "18px" }} />,
                },
            ]
            : []),
        {
            id: "formDepartment",
            label: "Department",
            name: "department",
            type: "dropdown",
            value: editedRow.department || "",
            icon: <BusinessIcon style={{ color: "#224D99", fontSize: "18px" }} />,
            options: allDepartments.map((dept) => ({
                value: dept?.title,
                label: dept?.title,
            })),
        },
        {
            id: "formRole",
            label: "Role",
            name: "roleId",
            type: "dropdown",
            value: editedRow.roleId || "",
            icon: <WorkIcon style={{ color: "#224D99", fontSize: "18px" }} />,
            options: roleIdWithName
                .filter(
                    (role) =>
                        role.id !== "organizationCoordinator" &&
                        role.id !== "platformCoordinator"
                )
                .map((role) => ({
                    value: role.id,
                    label: role.name,
                })),
        },
        {
            id: "formFeatures",
            label: "Features",
            name: "features",
            type: "multiselect",
            icon: <FeaturesIcon style={{ color: "#224D99", fontSize: "18px" }} />,
            options: filteredFeatures,
        },
    ];

    return (
        <>
            <Modal
                show={open}
                onHide={handleUserModalClose}
                size="lg"
                centered
                style={{ maxWidth: isMobile ? "95%" : isTablet ? "80%" : "lg" }}
            >
                <Modal.Header
                    closeButton
                    style={{
                        backgroundColor: "#f8f9fa",
                        borderBottom: "2px solid #e9ecef",
                    }}
                >
                    <Modal.Title
                        style={{
                            color: "#224D99",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        <PersonIcon style={{ fontSize: "24px" }} />
                        Edit User Details
                        {isLoadingEmployeeData && (
                            <Spinner animation="border" size="sm" className="ms-2" />
                        )}
                    </Modal.Title>
                </Modal.Header>

                <Modal.Body style={{ padding: isMobile ? "16px" : "24px" }}>
                    {selectRow && (
                        <Form>
                            <Row className="gx-3">
                                {formFields.map((field) => (
                                    <Form.Group
                                        as={Col}
                                        xs={12}
                                        md={field.name === "features" ? 12 : 6}
                                        controlId={field.id}
                                        key={field.id}
                                        className="mb-3"
                                    >
                                        <Form.Label
                                            className="d-flex align-items-center gap-2 fw-bold"
                                            style={{ color: "#224D99" }}
                                        >
                                            {field.icon}
                                            {field.label}
                                        </Form.Label>
                                        {field.type === "dropdown" ? (
                                            <Form.Control
                                                as={FormSelect}
                                                name={field.name}
                                                value={field.value}
                                                onChange={handleInputChange}
                                                placeholder={`Choose ${field.label}`}
                                                options={field.options}
                                                style={{
                                                    borderColor: "#224D99",
                                                    borderRadius: "8px",
                                                    padding: isMobile ? "10px" : "12px",
                                                }}
                                            />
                                        ) : field.type === "multiselect" ? (
                                               <Multiselect
        options={field.options}
        selectedValues={existingFeatures}
        onSelect={(selectedList, selectedItem) => setExistingFeatures(selectedList)}
        onRemove={(selectedList, removedItem) => setExistingFeatures(selectedList)}
        displayValue="label"
        placeholder={editedRow?.roleId ? "Search features" : "Select a role first"}
        disable={!editedRow?.roleId}
                                                                           showCheckbox={true}
                                                                    closeOnSelect={false}
 style={{
                                                                     option: {
                                                                       backgroundColor: '#f5f7fa', // very light gray blue
                                                                       color: '#2c3e50',           // dark slate blue
                                                                     },
                                                                     selectedOption: {
                                                                       backgroundColor: '#7fb3d5', // soft blue
                                                                       color: 'white',
                                                                     },
                                                                     chips: {
                                                                       backgroundColor: '#85cbfdff', // light blue-gray
                                                                       color: 'black',           // medium blue text
                                                                       fontWeight: '500'
                                                                     },
                                                                     searchBox: {
                                                                       border: '1px solid #ccd6e0',
                                                                       borderRadius: '6px',
                                                                       padding: '8px',
                                                                     },
                                                                     optionContainer: {
                                                                       maxHeight: '180px',
                                                                       overflowY: 'auto',
                                                                     },
                                                                   }}
      />
                                        ) : (
                                            <Form.Control
                                                type={field.type}
                                                name={field.name}
                                                value={field.value}
                                                onChange={handleInputChange}
                                                disabled={field.disabled}
                                                style={{
                                                    borderColor: "#224D99",
                                                    backgroundColor: field.disabled ? "#f8f9fa" : "white",
                                                    borderRadius: "8px",
                                                    padding: isMobile ? "10px" : "12px",
                                                }}
                                            />
                                        )}
                                    </Form.Group>
                                ))}
                            </Row>

                            <Form.Group controlId="formStatus" className="mt-4">
                                <div
                                    className="p-3 rounded"
                                    style={{ backgroundColor: "#f8f9fa", border: "2px solid #e9ecef" }}
                                >
                                    <div className="d-flex align-items-center justify-content-between">
                                        <div className="d-flex align-items-center gap-2">
                                            {editedRow.status === "active" ? (
                                                <ToggleOnIcon style={{ color: "#28a745", fontSize: "20px" }} />
                                            ) : (
                                                <ToggleOffIcon style={{ color: "#dc3545", fontSize: "20px" }} />
                                            )}
                                            <Form.Label className="mb-0 fw-bold" style={{ color: "#224D99" }}>
                                                User Status
                                            </Form.Label>
                                        </div>
                                        <div className="d-flex align-items-center gap-2">
                                            <span
                                                className={`fw-bold ${editedRow.status === "active" ? "text-success" : "text-danger"
                                                    }`}
                                                style={{ fontSize: isMobile ? "0.85rem" : "0.9rem" }}
                                            >
                                                {editedRow.status === "active" ? "ACTIVE" : "INACTIVE"}
                                            </span>
                                            <div className="form-check form-switch">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    role="switch"
                                                    id="statusToggle"
                                                    checked={editedRow.status === "active"}
                                                    onChange={handleStatusToggle}
                                                    style={{
                                                        backgroundColor:
                                                            editedRow.status === "active" ? "#28a745" : "#dc3545",
                                                        borderColor:
                                                            editedRow.status === "active" ? "#28a745" : "#dc3545",
                                                        cursor: "pointer",
                                                        width: isMobile ? "40px" : "48px",
                                                        height: isMobile ? "20px" : "24px",
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <small className="text-muted">
                                        Toggle to change user status between active and inactive
                                    </small>
                                </div>
                            </Form.Group>
                        </Form>
                    )}
                </Modal.Body>

                <Modal.Footer className="border-0 pt-2 d-flex gap-2">
                    <Button
                        variant="secondary"
                        onClick={handleUserModalClose}
                        disabled={isSubmitting || isLoadingEmployeeData}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSaveClick}
                        disabled={isSubmitting || isLoadingEmployeeData}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />
                                Saving...
                            </>
                        ) : (
                            "Update User"
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={showStatusConfirmation}
                onHide={() => setShowStatusConfirmation(false)}
                centered
                style={{ maxWidth: isMobile ? "95%" : isTablet ? "80%" : "lg" }}
            >
                <Modal.Header
                    closeButton
                    style={{ backgroundColor: "#fff3cd", borderBottom: "2px solid #ffeaa7" }}
                >
                    <Modal.Title
                        style={{
                            color: "#856404",
                            fontWeight: "600",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                        }}
                    >
                        {editedRow.status === "active" ? (
                            <ToggleOnIcon style={{ color: "#28a745" }} />
                        ) : (
                            <ToggleOffIcon style={{ color: "#dc3545" }} />
                        )}
                        Confirm Status Change
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="text-center">
                        <p className="mb-3">
                            Are you sure you want to{" "}
                            <strong style={{ color: editedRow.status === "active" ? "#28a745" : "#dc3545" }}>
                                {editedRow.status === "active" ? "ACTIVATE" : "DEACTIVATE"}
                            </strong>{" "}
                            this user?
                        </p>
                        <div
                            className="p-3 rounded"
                            style={{ backgroundColor: "#f8f9fa", border: "1px solid #dee2e6" }}
                        >
                            <strong>User: {editedRow.name}</strong>
                            <br />
                            <small className="text-muted">{editedRow.email}</small>
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="secondary"
                        onClick={() => setShowStatusConfirmation(false)}
                        disabled={isSubmitting}
                        style={{
                            padding: isMobile ? "8px 16px" : "10px 20px",
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant={editedRow.status === "active" ? "success" : "danger"}
                        onClick={() => {
                            setShowStatusConfirmation(false);
                            handleSave();
                        }}
                        disabled={isSubmitting}
                        style={{
                            minWidth: isMobile ? "100px" : "120px",
                            padding: isMobile ? "8px 16px" : "10px 20px",
                        }}
                    >
                        {isSubmitting ? (
                            <Spinner animation="border" size="sm" />
                        ) : (
                            `${editedRow.status === "active" ? "Activate" : "Deactivate"} User`
                        )}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default EditUserModal;